import React, { useState } from 'react'
import Preloader from '../components/Preloader'
import Header from '../components/Header'
import ScrollReveal from '../components/ScrollReveal'
import Footer from '../components/Footer'
import Tooltip from '@mui/material/Tooltip'
import { Link } from 'react-router'
import { FaInstagram, FaLinkedinIn, FaSquareXTwitter, FaWhatsapp } from 'react-icons/fa6'
import { RiTiktokLine } from 'react-icons/ri'
import { FiHelpCircle } from 'react-icons/fi'

const faqsData = [
    {
        question: 'What is Virtual Reality (VR)?',
        answer: 'Virtual Reality is a computer-generated 3D environment that immerses users in a simulated experience, often using a headset and motion controllers.'
    },
    {
        question: 'What equipment do I need to experience VR?',
        answer: 'You typically need a VR headset, compatible hardware (like a PC, console, or standalone device), and sometimes motion controllers or sensors for interaction.'
    },
    {
        question: 'Is VR safe to use for long periods?',
        answer: 'Moderate VR use is generally safe, but extended sessions can cause eye strain, dizziness, or motion sickness. It is recommended to take breaks every 20–30 minutes.'
    },
    {
        question: 'How is VR different from Augmented Reality (AR)?',
        answer: 'VR creates a fully immersive digital world, while AR overlays digital elements onto the real world through devices like smartphones or AR glasses.'
    },
    {
        question: 'What are the main applications of VR besides gaming?',
        answer: 'VR is used in education, healthcare, architecture, real estate, remote collaboration, therapy, and even tourism to create immersive and interactive experiences.'
    },
    {
        question: 'Can children use Virtual Reality?',
        answer: 'Most VR headset manufacturers recommend use for ages 12 and above due to eye development and safety concerns. Parents should supervise usage and limit session duration.'
    },
    {
        question: 'Do I need a powerful computer to run VR?',
        answer: 'High-end VR headsets require a powerful gaming PC with a strong GPU and CPU, while standalone headsets like Meta Quest do not need a PC to run.'
    },
    {
        question: 'Can VR be used for fitness and exercise?',
        answer: 'Yes! VR fitness apps and games provide engaging workouts, from boxing and dancing to guided meditation, making exercise more fun and interactive.'
    },
    {
        question: 'Is VR affordable for home use?',
        answer: 'VR is becoming more affordable, with options ranging from budget-friendly standalone headsets to premium devices for advanced experiences.'
    },
    {
        question: 'What does the future of VR look like?',
        answer: 'The future of VR includes more realistic graphics, lighter headsets, improved haptic feedback, social virtual spaces, and wider adoption in industries like education and medicine.'
    }
]

const socialLinks = [
    { title: 'LinkedIn', href: 'https://www.linkedin.com/company/vrify-technologies', icon: <FaLinkedinIn className="text-lg" /> },
    { title: 'X (formerly Twitter)', href: 'https://x.com/VrifyTech?t=WcnaecQOz1HMkjra3Bpd7A&s=08', icon: <FaSquareXTwitter className="text-lg" /> },
    { title: 'Instagram', href: 'https://www.instagram.com/vrifytechnologies?igsh=MTdpbWl4eGFibjVmbA=', icon: <FaInstagram className="text-lg" /> },
    { title: 'WhatsApp', href: 'https://wa.me/c/233544847395', icon: <FaWhatsapp className="text-lg" /> },
    { title: 'TikTok', href: 'https://www.tiktok.com/@vrifytechnologies?_t=ZM-90BY6Oc2wHr&_r=1', icon: <RiTiktokLine className="text-lg" /> }
]

const Faqs = () => {
    const [openIndex, setOpenIndex] = useState(null)

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? null : index)
    }

    return (
        <div>
            <div className="font-nanum-myeongjo min-h-screen bg-slate-950 text-slate-100 pt-16 md:pt-20 lg:pt-16 relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(250,204,21,0.22),_transparent_24%),radial-gradient(circle_at_bottom_right,_rgba(59,130,246,0.16),_transparent_24%)]" />

                <div className="relative z-10">
                    <Header />
                    <Preloader />

                    <section className="mx-auto max-w-7xl px-4 pb-8 pt-8 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="max-w-3xl">
                                <p className="inline-flex items-center gap-2 rounded-full border border-yellow-400/50 bg-yellow-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">
                                    <FiHelpCircle className="text-sm" />
                                    FAQs
                                </p>
                                <h1 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
                                    Everything you need to know about immersive experiences.
                                </h1>
                                <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
                                    Browse the most common questions about VR, AR, and how VRIFY helps businesses create memorable digital experiences.
                                </p>
                            </div>
                        </ScrollReveal>

                        <div className="mt-8 grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-start">
                            <ScrollReveal>
                                <div className="rounded-[28px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm sm:p-6">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">Need more help?</p>
                                    <h2 className="mt-3 text-2xl font-bold text-white">Not finding what you need?</h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-300">
                                        Reach out to our team for guidance on planning your next VR or AR engagement.
                                    </p>
                                    <div className="mt-5 flex flex-wrap gap-3">
                                        <a href='/contactus' className='inline-flex items-center justify-center rounded-full bg-yellow-400 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-yellow-300'>
                                            Contact us
                                        </a>
                                        {/* <a href='/' className='inline-flex items-center justify-center rounded-full border border-slate-700 px-5 py-3 text-sm font-semibold text-white transition hover:border-yellow-300 hover:text-yellow-200'>
                                            Back home
                                        </a> */}
                                    </div>
                                </div>
                            </ScrollReveal>

                            <ScrollReveal>
                                <div className="rounded-[28px] border border-white/10 bg-white/5 p-5 backdrop-blur-sm sm:p-6">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-yellow-300">General</p>
                                            <h2 className="mt-2 text-2xl font-bold text-white">Frequently asked questions</h2>
                                        </div>
                                    </div>

                                    <div className="mt-6 space-y-3">
                                        {faqsData.map((faq, index) => {
                                            const isOpen = openIndex === index

                                            return (
                                                <div key={faq.question} className="overflow-hidden rounded-2xl border border-white/10 bg-slate-950/70">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleAccordion(index)}
                                                        className="flex w-full items-center justify-between gap-4 px-4 py-4 text-left"
                                                    >
                                                        <span className="text-sm font-semibold text-white sm:text-base">{faq.question}</span>
                                                        <span className="text-2xl text-yellow-300">{isOpen ? '−' : '+'}</span>
                                                    </button>
                                                    {isOpen && (
                                                        <div className="border-t border-white/10 px-4 py-4 text-sm leading-7 text-slate-200">
                                                            <p>{faq.answer}</p>
                                                            <div className="mt-4 flex flex-wrap gap-3">
                                                                {socialLinks.map((item) => (
                                                                    <Tooltip title={item.title} arrow key={item.title}>
                                                                        <Link
                                                                            to={item.href}
                                                                            target="_blank"
                                                                            rel="noreferrer"
                                                                            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 px-3 py-2 text-white transition hover:border-yellow-300 hover:text-yellow-200"
                                                                        >
                                                                            {item.icon}
                                                                        </Link>
                                                                    </Tooltip>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </ScrollReveal>
                        </div>
                    </section>

                    <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
                        <ScrollReveal>
                            <div className="rounded-[30px] border border-yellow-300/30 bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-200 px-5 py-8 sm:px-8 lg:flex lg:items-center lg:justify-between lg:px-10">
                                <div className="max-w-2xl">
                                    <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-950">Still have questions?</p>
                                    <h2 className="mt-3 text-2xl font-bold text-slate-950 sm:text-3xl">Let’s help you choose the right immersive solution.</h2>
                                    <p className="mt-3 text-sm leading-7 text-slate-900/80 sm:text-base">
                                        Our team can help you compare options, plan your rollout, and shape the right experience for your audience.
                                    </p>
                                </div>
                                <div className="mt-6 flex flex-wrap gap-3 lg:mt-0">
                                    <a href='/contactus' className='inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-bold text-yellow-300 transition hover:bg-slate-900'>
                                        Contact us
                                    </a>
                                    <a href='/virtual-tours' className='inline-flex items-center justify-center rounded-full border border-slate-950 px-5 py-3 text-sm font-bold text-slate-950 transition hover:bg-slate-950 hover:text-yellow-300'>
                                        Explore services
                                    </a>
                                </div>
                            </div>
                        </ScrollReveal>
                    </section>
                </div>
            </div>

            <Footer />
        </div>
    )
}

export default Faqs
